import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { TruckIcon } from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { fetchDrivingDriverByUser } from "../../../../api/driving/driver";
import { driverVehicleAction } from "../drivers/utils";
import VehiclesListDialog from "../../vehicles/list-dialog";
import { useUserHasAccess } from "../../../../routes/roleOnly";

const DRIVER_FIELDS =
  "id,displayTitle,checked_into_vehicle.id,checked_into_vehicle.displayTitle,checked_into_vehicle.number_plate,checked_into_vehicle.distance_driven_today,checked_into_vehicle.distance_driven_this_week,checked_into_vehicle.distance_driven_this_month";

/**
 * Stable sidebar path → redirects to `/driving/vehicles/:id/handbook` when the user is checked into a vehicle.
 */
export default function VehicleHandbookEntry() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const hasVehiclesRead = useUserHasAccess({ role: "vehicles", action: "read" });

  const [phase, setPhase] = useState("loading"); // loading | redirecting | no_vehicle | error
  const [driver, setDriver] = useState(null);
  const [vehiclesListDialogOpen, setVehiclesListDialogOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const automileCheckinFilter = Boolean(currentCompany?.automile_ready);
  const checkInVehicleFilters = [
    { name: "active", value: true },
    { name: "available_for_driver_checkin", value: true },
    ...(automileCheckinFilter ? [{ name: "synced_with_automile", value: true }] : []),
  ];

  useEffect(() => {
    if (!currentUser?.id) return undefined;

    let cancelled = false;

    (async () => {
      setPhase("loading");
      try {
        const res = await fetchDrivingDriverByUser({
          user_id: currentUser.id,
          fields: DRIVER_FIELDS,
        });
        if (cancelled) return;

        const vid = res?.data?.checked_into_vehicle?.id;
        if (vid) {
          setPhase("redirecting");
          setDriver(null);
          navigate(`/dashboard/driving/vehicles/${vid}/handbook`, {
            replace: true,
          });
          return;
        }

        const d = res?.data?.id ? res.data : null;
        setDriver(d);
        setPhase("no_vehicle");
      } catch (e) {
        if (!cancelled) {
          setDriver(null);
          setPhase("error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, navigate, refreshTick]);

  const handleVehicleCheckin = async (vehicleId) => {
    if (!driver?.id) return;
    await driverVehicleAction({
      userId: currentUser?.id,
      vehicleId,
      action: "checkin",
    });
    setVehiclesListDialogOpen(false);
    setRefreshTick((v) => v + 1);
  };

  const canShowCheckIn = hasVehiclesRead && driver?.id && phase === "no_vehicle";

  const headerMainButton =
    canShowCheckIn
      ? {
          label: t("Vehicle handbook check in"),
          action: () => setVehiclesListDialogOpen(true),
          path: null,
          color: "",
          icon: TruckIcon,
        }
      : undefined;

  if (phase === "loading" || phase === "redirecting") {
    return (
      <DashboardHeader variant="v2" title={t("Vehicle handbook")} backUrl="/dashboard/driving/trips">
        <ResourceLoading />
      </DashboardHeader>
    );
  }

  if (phase === "error") {
    return (
      <DashboardHeader variant="v2" title={t("Vehicle handbook")} backUrl="/dashboard/driving/trips">
        <div className="mx-auto max-w-7xl px-4 py-5 pb-[calc(5rem+env(safe-area-inset-bottom,0px))]">
          <p className="text-sm text-slate-600">
            {t("Vehicle handbook could not be opened. Try again or contact an administrator.")}
          </p>
        </div>
      </DashboardHeader>
    );
  }

  return (
    <>
      <DashboardHeader
        variant="v2"
        title={t("Vehicle handbook")}
        subtitle={t("Vehicle handbook entry subtitle")}
        backUrl="/dashboard/driving/trips"
        mainButton={headerMainButton}
      >
        <div className="mx-auto max-w-7xl px-4 py-5 pb-[calc(5rem+env(safe-area-inset-bottom,0px))]">
          <div className="max-w-lg space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-700">
              {t("Check in to a vehicle to open its handbook from this menu.")}
            </p>
            {!driver?.id ? (
              <p className="text-sm text-slate-600">{t("Vehicle handbook not a driver")}</p>
            ) : null}
            {driver?.id && !hasVehiclesRead ? (
              <p className="text-sm text-slate-600">{t("Vehicle handbook no vehicles permission")}</p>
            ) : null}
            {canShowCheckIn ? (
              <p className="text-sm text-slate-600">{t("Vehicle handbook check in hint")}</p>
            ) : null}
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              {canShowCheckIn ? (
                <button
                  type="button"
                  onClick={() => setVehiclesListDialogOpen(true)}
                  className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                >
                  {t("Vehicle handbook check in")}
                </button>
              ) : null}
              <Link
                to="/dashboard/driving/trips"
                className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                {t("Go to trips")}
              </Link>
            </div>
          </div>
        </div>
      </DashboardHeader>

      {canShowCheckIn ? (
        <VehiclesListDialog
          open={vehiclesListDialogOpen}
          onClose={() => setVehiclesListDialogOpen(false)}
          checkInMode
          filters={checkInVehicleFilters}
          onSelect={handleVehicleCheckin}
        />
      ) : null}
    </>
  );
}
